import React from 'react';
import {
  Box,
  Typography,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Grid,
  useMediaQuery,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import TapIcon from '@mui/icons-material/TouchApp';
import { SELECTED_BG, SELECTED_FG, SELECTED_BG_HOVER } from '../config/uiTheme';

const SECTION_LABEL_SX = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'rgba(255, 255, 255, 0.45)',
  mb: 1,
};

const SLIDER_SX = {
  color: '#fff',
  '& .MuiSlider-thumb': {
    backgroundColor: '#fff',
  },
  '& .MuiSlider-track': {
    backgroundColor: '#fff',
  },
  '& .MuiSlider-rail': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
};

const toggleSx = (enabled) => ({
  backgroundColor: enabled ? SELECTED_BG : 'rgba(255, 255, 255, 0.08)',
  color: enabled ? SELECTED_FG : '#fff',
  '&:hover': {
    backgroundColor: enabled ? SELECTED_BG_HOVER : 'rgba(255, 255, 255, 0.14)',
  },
});

const MetronomeSettings = ({
  metronomeEnabled,
  metronomeBPM,
  metronomeTimeSignature,
  metronomeVolume,
  metronomeVisualOnly,
  onMetronomeToggle,
  onMetronomeBPMChange,
  onMetronomeTimeSignatureChange,
  onMetronomeVolumeChange,
  onMetronomeVisualOnlyChange,
  onTapTempo,
  barsPerChord,
  onBarsPerChordChange
}) => {
  // Telephone en paysage : la hauteur, pas l'orientation, est ce qui contraint.
  // Sur ce format on serre tout et on passe en deux colonnes.
  const isCompact = useMediaQuery('(max-height: 500px)');
  const isWide = useMediaQuery('(min-width: 900px)');

  return (
    <Box
      sx={{
        p: isCompact ? 1.5 : 2,
        minWidth: isCompact ? 420 : (isWide ? 500 : 300),
        maxWidth: isCompact ? 'min(560px, 92vw)' : (isWide ? 600 : 350),
        maxHeight: '85dvh',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      {isCompact ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ ...SECTION_LABEL_SX, mb: 0 }}>
            Metronome
          </Typography>
          <IconButton
            onClick={onMetronomeToggle}
            size="small"
            sx={{
              ...toggleSx(metronomeEnabled),
              borderRadius: 1,
            }}
          >
            {metronomeEnabled ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          </IconButton>
        </Box>
      ) : (
        <>
          <Typography sx={SECTION_LABEL_SX}>
            Metronome
          </Typography>

          {/* Toggle Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Button
              variant="contained"
              disableElevation
              onClick={onMetronomeToggle}
              startIcon={metronomeEnabled ? <StopIcon /> : <PlayArrowIcon />}
              sx={{
                ...toggleSx(metronomeEnabled),
                minWidth: '140px'
              }}
            >
              Toggle Metronome
            </Button>
          </Box>
        </>
      )}

      {/* Controls Grid */}
      <Grid container spacing={isCompact ? 2 : (isWide ? 3 : 0)}>
        {/* Left Column */}
        <Grid item xs={isCompact ? 6 : 12} md={6}>
          {/* BPM Control */}
          <Box sx={{ mb: isCompact ? 1.5 : 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem' }}>
                BPM
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ color: '#fff', fontWeight: 'bold', minWidth: '35px' }}>
                  {metronomeBPM}
                </Typography>
                <IconButton
                  onClick={onTapTempo}
                  size="small"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': {
                      color: '#fff',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  <TapIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Slider
              value={metronomeBPM}
              onChange={(e, newValue) => onMetronomeBPMChange && onMetronomeBPMChange(newValue)}
              min={60}
              max={200}
              size={isCompact ? 'small' : 'medium'}
              sx={SLIDER_SX}
            />
          </Box>

          {/* Volume Control */}
          <Box sx={{ mb: (isCompact || isWide) ? 0 : 3 }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem', mb: isCompact ? 0.5 : 1 }}>
              Volume - {Math.round(metronomeVolume * 100)}%
            </Typography>
            <Slider
              value={metronomeVolume}
              onChange={(e, newValue) => onMetronomeVolumeChange && onMetronomeVolumeChange(newValue)}
              min={0}
              max={1}
              step={0.01}
              size={isCompact ? 'small' : 'medium'}
              sx={SLIDER_SX}
            />
          </Box>
        </Grid>

        {/* Right Column */}
        <Grid item xs={isCompact ? 6 : 12} md={6}>
          {/* Time Signature */}
          <Box sx={{ mb: isCompact ? 1.5 : 2 }}>
            <FormControl fullWidth size={isCompact ? 'small' : 'medium'}>
              <InputLabel 
                id="time-signature-label"
                sx={{ color: 'rgba(255, 255, 255, 0.9)', '&.Mui-focused': { color: '#fff' } }}
              >
                Time Signature
              </InputLabel>
              <Select
                labelId="time-signature-label"
                value={metronomeTimeSignature}
                label="Time Signature"
                onChange={(e) => onMetronomeTimeSignatureChange && onMetronomeTimeSignatureChange(e.target.value)}
                sx={{
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.12)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#fff',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#15151A',
                      backgroundImage: 'none',
                      borderRadius: '14px',
                      maxHeight: isCompact ? '200px' : '300px',
                      '& .MuiMenuItem-root': {
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(255, 255, 255, 0.14)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          }
                        }
                      }
                    }
                  }
                }}
              >
                <MenuItem value="4/4">4/4</MenuItem>
                <MenuItem value="3/4">3/4</MenuItem>
                <MenuItem value="2/4">2/4</MenuItem>
                <MenuItem value="5/4">5/4</MenuItem>
                <MenuItem value="7/4">7/4</MenuItem>
                <MenuItem value="6/8">6/8</MenuItem>
                <MenuItem value="5/8">5/8</MenuItem>
                <MenuItem value="7/8">7/8</MenuItem>
                <MenuItem value="9/8">9/8</MenuItem>
                <MenuItem value="11/8">11/8</MenuItem>
                <MenuItem value="12/8">12/8</MenuItem>
                <MenuItem value="13/8">13/8</MenuItem>
                <MenuItem value="15/8">15/8</MenuItem>
                <MenuItem value="21/8">21/8</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Bars Per Chord Control */}
          <Box sx={{ mb: 0 }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem', mb: isCompact ? 0.5 : 1 }}>
              Change chord every {barsPerChord} bars
            </Typography>
            <Slider
              value={barsPerChord}
              onChange={(e, newValue) => onBarsPerChordChange && onBarsPerChordChange(newValue)}
              min={1}
              max={16}
              step={1}
              size={isCompact ? 'small' : 'medium'}
              marks={[
                { value: 1, label: '1' },
                { value: 4, label: '4' },
                { value: 8, label: '8' },
                { value: 16, label: '16' }
              ]}
              sx={{
                ...SLIDER_SX,
                '& .MuiSlider-mark': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                },
                '& .MuiSlider-markLabel': {
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.75rem',
                }
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MetronomeSettings;